'use client'

import { useAuth } from '@/components/AuthProvider'
import { Button } from '@/components/ui/Button'
import { authedFetch } from '@/lib/api'
import { validatePassword, validateEmail, sanitizeInput } from '@/lib/validation'
import { useRouter } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  CreditCard, Eye, EyeOff, Gift, Lock, LogOut, Mail, UserCircle, Users,
} from 'lucide-react'
import { ReferFriendModal, OPEN_REFERRAL_EVENT } from '@/components/ReferFriendModal'

const apiBase = () => (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '')

export function ProfileDropdown() {
  const { user, logout, refreshUser } = useAuth()
  const router = useRouter()

  const [isOpen, setIsOpen] = useState(false)
  const [userHasPassword, setUserHasPassword] = useState(false)
  const [isAffiliate, setIsAffiliate] = useState(false)
  const [subPortalLoading, setSubPortalLoading] = useState(false)

  // Logout
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const [isLogoutLoading, setIsLogoutLoading] = useState(false)

  // Set/Change password
  const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false)
  const [spPassword, setSpPassword] = useState('')
  const [spConfirmPassword, setSpConfirmPassword] = useState('')
  const [spOldPassword, setSpOldPassword] = useState('')
  const [spError, setSpError] = useState<string | null>(null)
  const [spSuccess, setSpSuccess] = useState(false)
  const [spLoading, setSpLoading] = useState(false)
  const [spShowPassword, setSpShowPassword] = useState(false)
  const [spShowConfirm, setSpShowConfirm] = useState(false)
  const [spShowOldPassword, setSpShowOldPassword] = useState(false)
  const [spForgotMode, setSpForgotMode] = useState(false)
  const [spResetCode, setSpResetCode] = useState('')
  const [spResetStep, setSpResetStep] = useState<'code' | 'newPassword'>('code')
  const [spResetCodeSent, setSpResetCodeSent] = useState(false)

  // Change email
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false)
  const [ceStep, setCeStep] = useState<'email' | 'code'>('email')
  const [ceNewEmail, setCeNewEmail] = useState('')
  const [ceConfirmEmail, setCeConfirmEmail] = useState('')
  const [ceCode, setCeCode] = useState('')
  const [ceError, setCeError] = useState<string | null>(null)
  const [ceSuccess, setCeSuccess] = useState(false)
  const [ceLoading, setCeLoading] = useState(false)
  const [ceChangesRemaining, setCeChangesRemaining] = useState<number | null>(null)

  // Whether the account has a password, and whether it's an affiliate, only
  // matter inside the menu, so they're fetched the first time it opens. This
  // used to run on every page load for every signed-in user: two extra API
  // calls each time, one of them to the account lookup, which is limited to
  // 5 requests a second across all users (see the stage settings in the CDK
  // stack).
  const menuDetailsLoadedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen || !user?.email || menuDetailsLoadedFor.current === user.email) return
    menuDetailsLoadedFor.current = user.email
    fetch(`${apiBase()}/auth/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email.trim().toLowerCase() }),
    }).then(r => r.json()).then(d => { if (d?.passwordSet === true) setUserHasPassword(true) }).catch(() => {})
    // Check affiliate status
    ;(async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth')
        const session = await fetchAuthSession()
        const token = session.tokens?.idToken?.toString()
        if (!token) return
        const res = await fetch(`${apiBase()}/affiliate/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
        if (res.ok) { const d = await res.json(); setIsAffiliate(d?.linked === true) }
      } catch {}
    })()
  }, [isOpen, user?.email])

  const openPasswordModal = () => {
    setIsOpen(false)
    setSpPassword(''); setSpConfirmPassword(''); setSpOldPassword('')
    setSpError(null); setSpSuccess(false); setSpShowPassword(false)
    setSpShowConfirm(false); setSpShowOldPassword(false)
    setSpForgotMode(false); setSpResetCode(''); setSpResetStep('code'); setSpResetCodeSent(false)
    setIsSetPasswordModalOpen(true)
  }

  const openEmailModal = async () => {
    setIsOpen(false)
    setCeStep('email'); setCeNewEmail(''); setCeConfirmEmail(''); setCeCode('')
    setCeError(null); setCeSuccess(false); setCeChangesRemaining(null)
    setIsChangeEmailModalOpen(true)
    try {
      const res = await authedFetch('/users/me')
      if (res.ok) { const d = await res.json(); setCeChangesRemaining(3 - (d.emailChangeCount || 0)) }
    } catch {}
  }

  // Deep-link support: ?openChangeEmail=1 auto-opens the change-email modal
  // (used by the extension's Career Bank "Update in app" link, since email
  // changes go through Cognito and can't be done from the extension directly).
  // Reads window.location directly (rather than useSearchParams) because this
  // component is mounted without a Suspense boundary on 20+ pages, and
  // useSearchParams requires one under static export (output: 'export').
  useEffect(() => {
    if (!user) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('openChangeEmail') !== '1') return
    void openEmailModal()
    params.delete('openChangeEmail')
    router.replace(params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Same-page trigger: other in-app buttons (e.g. Career Bank's "Change Email"
  // row) that live on a page where this dropdown is already mounted dispatch
  // this event instead of navigating via the ?openChangeEmail=1 query param —
  // avoids a pointless full-page reload when we're already right here.
  useEffect(() => {
    const handler = () => { void openEmailModal() }
    window.addEventListener('cvcircuit:openChangeEmail', handler)
    return () => window.removeEventListener('cvcircuit:openChangeEmail', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Must stay below every hook above. This used to sit before the last two
  // effects, so the render where logout set `user` to null ran two fewer
  // hooks than the one before it — React throws on that ("rendered fewer
  // hooks than expected"), which was the logout crash into the error page.
  if (!user) return null

  return (
    <>
      {/* Profile button + dropdown */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
          aria-label="Profile menu"
        >
          <UserCircle className="w-6 h-6" />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-[70] py-1">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Signed in as</p>
                <p className="text-sm font-medium text-gray-800 truncate">{user.email}</p>
                <p className="text-xs text-gray-400 mt-0.5">{user.subscriptionTier === 'subscriber' ? 'Pro' : 'Free'}</p>
              </div>
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                onClick={openPasswordModal}
              >
                <Lock className="w-4 h-4" />
                {userHasPassword ? 'Change Password' : 'Set Password'}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                onClick={openEmailModal}
              >
                <Mail className="w-4 h-4" />
                Change Email
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                onClick={() => { setIsOpen(false); window.dispatchEvent(new Event(OPEN_REFERRAL_EVENT)) }}
              >
                <Gift className="w-4 h-4" />
                Refer a Friend
              </button>
              {user.subscriptionTier === 'subscriber' && user.proSource !== 'referral' && (
                <button
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  disabled={subPortalLoading}
                  onClick={async () => {
                    setIsOpen(false); setSubPortalLoading(true)
                    try {
                      const { fetchAuthSession } = await import('aws-amplify/auth')
                      const session = await fetchAuthSession()
                      const token = session.tokens?.idToken?.toString()
                      if (!token) return
                      const res = await fetch(`${apiBase()}/billing/portal`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
                      const body = await res.json().catch(() => null)
                      if (res.ok && body?.url) window.location.href = body.url
                    } catch {} finally { setSubPortalLoading(false) }
                  }}
                >
                  <CreditCard className="w-4 h-4" />
                  {subPortalLoading ? 'Loading…' : 'Manage Subscription'}
                </button>
              )}
              {isAffiliate && (
                <button
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  onClick={() => { window.location.href = '/affiliate' }}
                >
                  <Users className="w-4 h-4" />
                  Affiliate Dashboard
                </button>
              )}
              <hr className="my-1 border-gray-200" />
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                onClick={() => { setIsOpen(false); setIsLogoutModalOpen(true) }}
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </>
        )}
      </div>

      <ReferFriendModal />

      {/* ── Logout Modal ── */}
      {isLogoutModalOpen && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-8">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold text-gray-800 mb-2">Log out?</h2>
            <p className="text-sm text-gray-600 mb-5">Are you sure you want to log out?</p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setIsLogoutModalOpen(false)} disabled={isLogoutLoading}>Cancel</Button>
              <Button onClick={async () => {
                setIsLogoutLoading(true)
                try { await logout(); router.replace('/') } finally { setIsLogoutLoading(false); setIsLogoutModalOpen(false) }
              }} disabled={isLogoutLoading}>{isLogoutLoading ? 'Logging out…' : 'Confirm Logout'}</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Set / Change Password Modal ── */}
      {isSetPasswordModalOpen && createPortal(
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-gray-800">
              {spForgotMode ? 'Reset Password' : userHasPassword ? 'Change Password' : 'Set Password'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {spForgotMode
                ? 'Enter the verification code sent to your email to reset your password.'
                : userHasPassword
                  ? 'Enter your current password and choose a new one.'
                  : 'Add a password for quicker login instead of waiting for a verification code email each time.'}
            </p>

            {spSuccess ? (
              <div className="mt-4">
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm">
                  {userHasPassword ? 'Password changed successfully!' : 'Password set successfully! You will be asked to enter it next time you log in.'}
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => { setIsSetPasswordModalOpen(false); setUserHasPassword(true) }}>Close</Button>
                </div>
              </div>
            ) : spForgotMode ? (
              <div className="mt-4 space-y-4">
                {spError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{spError}</div>}
                {!spResetCodeSent ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">We will send a verification code to <strong>{user.email}</strong> to reset your password.</p>
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => { setSpForgotMode(false); setSpError(null) }} disabled={spLoading}>Back</Button>
                      <Button onClick={async () => {
                        setSpError(null); setSpLoading(true)
                        try {
                          const res = await authedFetch('/auth/reset-password-start', { method: 'POST' })
                          if (!res.ok) { const d = await res.json().catch(() => ({})); setSpError(d.error || 'Failed to send reset code.'); return }
                          setSpResetCodeSent(true); setSpResetStep('code')
                        } catch { setSpError('Failed to send reset code. Please try again.') } finally { setSpLoading(false) }
                      }} disabled={spLoading}>{spLoading ? 'Sending...' : 'Send Reset Code'}</Button>
                    </div>
                  </div>
                ) : spResetStep === 'code' ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded text-sm">A verification code has been sent to <strong>{user.email}</strong>.</div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Verification Code</label>
                      <input type="text" inputMode="numeric" value={spResetCode} onChange={(e) => { setSpResetCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setSpError(null) }}
                        className="mt-1 text-gray-600 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest"
                        placeholder="000000" maxLength={6} />
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => { setSpResetCodeSent(false); setSpResetCode(''); setSpError(null) }} disabled={spLoading}>Back</Button>
                      <Button onClick={async () => {
                        if (!/^\d{6}$/.test(spResetCode.trim())) { setSpError('Please enter a valid 6-digit code.'); return }
                        setSpLoading(true); setSpError(null)
                        try {
                          const res = await authedFetch('/auth/verify-reset-code', { method: 'POST', body: JSON.stringify({ code: spResetCode.trim() }) })
                          if (!res.ok) { const d = await res.json().catch(() => ({})); setSpError(d.error || 'Incorrect verification code.'); return }
                          setSpResetStep('newPassword'); setSpError(null)
                        } catch { setSpError('Failed to verify code. Please try again.') } finally { setSpLoading(false) }
                      }} disabled={spLoading || spResetCode.length !== 6}>{spLoading ? 'Verifying...' : 'Verify Code'}</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">New Password</label>
                      <div className="relative">
                        <input type={spShowPassword ? 'text' : 'password'} value={spPassword} onChange={(e) => { setSpPassword(e.target.value); setSpError(null) }}
                          className="mt-1 text-gray-600 block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter new password" maxLength={128} />
                        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSpShowPassword(!spShowPassword)} tabIndex={-1}>
                          {spShowPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                      <div className="relative">
                        <input type={spShowConfirm ? 'text' : 'password'} value={spConfirmPassword} onChange={(e) => { setSpConfirmPassword(e.target.value); setSpError(null) }}
                          className="mt-1 text-gray-600 block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Confirm new password" maxLength={128} />
                        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSpShowConfirm(!spShowConfirm)} tabIndex={-1}>
                          {spShowConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <PasswordStrength password={spPassword} confirmPassword={spConfirmPassword} />
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => { setSpResetStep('code'); setSpError(null) }} disabled={spLoading}>Back</Button>
                      <Button onClick={async () => {
                        setSpError(null)
                        const v = validatePassword(spPassword)
                        if (!v.valid) { setSpError(v.error || 'Invalid password'); return }
                        if (spPassword !== spConfirmPassword) { setSpError('Passwords do not match'); return }
                        setSpLoading(true)
                        try {
                          const res = await authedFetch('/auth/reset-password-confirm', { method: 'POST', body: JSON.stringify({ code: spResetCode.trim(), newPassword: spPassword, confirmPassword: spConfirmPassword }) })
                          if (!res.ok) { const d = await res.json().catch(() => ({})); setSpError(d.error || 'Failed to reset password.'); return }
                          setSpSuccess(true)
                        } catch { setSpError('Failed to reset password. Please try again.') } finally { setSpLoading(false) }
                      }} disabled={spLoading || !spPassword || !spConfirmPassword}>{spLoading ? 'Resetting...' : 'Reset Password'}</Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {spError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{spError}</div>}
                {userHasPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Password</label>
                    <div className="relative">
                      <input type={spShowOldPassword ? 'text' : 'password'} value={spOldPassword} onChange={(e) => { setSpOldPassword(e.target.value); setSpError(null) }}
                        className="mt-1 text-gray-600 block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter current password" maxLength={128} />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSpShowOldPassword(!spShowOldPassword)} tabIndex={-1}>
                        {spShowOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">{userHasPassword ? 'New Password' : 'Password'}</label>
                  <div className="relative">
                    <input type={spShowPassword ? 'text' : 'password'} value={spPassword} onChange={(e) => { setSpPassword(e.target.value); setSpError(null) }}
                      className="mt-1 text-gray-600 block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder={userHasPassword ? 'Enter new password' : 'Enter password'} maxLength={128} />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSpShowPassword(!spShowPassword)} tabIndex={-1}>
                      {spShowPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{userHasPassword ? 'Confirm New Password' : 'Confirm Password'}</label>
                  <div className="relative">
                    <input type={spShowConfirm ? 'text' : 'password'} value={spConfirmPassword} onChange={(e) => { setSpConfirmPassword(e.target.value); setSpError(null) }}
                      className="mt-1 text-gray-600 block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder={userHasPassword ? 'Confirm new password' : 'Confirm password'} maxLength={128} />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSpShowConfirm(!spShowConfirm)} tabIndex={-1}>
                      {spShowConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <PasswordStrength password={spPassword} confirmPassword={spConfirmPassword} />
                {userHasPassword && (
                  <button type="button" className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                    onClick={() => { setSpForgotMode(true); setSpError(null); setSpPassword(''); setSpConfirmPassword(''); setSpOldPassword('') }}>
                    Forgot your password?
                  </button>
                )}
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setIsSetPasswordModalOpen(false)} disabled={spLoading}>Cancel</Button>
                  <Button onClick={async () => {
                    setSpError(null)
                    if (userHasPassword && !spOldPassword) { setSpError('Please enter your current password'); return }
                    const v = validatePassword(spPassword)
                    if (!v.valid) { setSpError(v.error || 'Invalid password'); return }
                    if (spPassword !== spConfirmPassword) { setSpError('Passwords do not match'); return }
                    setSpLoading(true)
                    try {
                      if (userHasPassword) {
                        const res = await authedFetch('/auth/change-password', { method: 'POST', body: JSON.stringify({ oldPassword: spOldPassword, newPassword: spPassword, confirmPassword: spConfirmPassword }) })
                        if (!res.ok) { const d = await res.json().catch(() => ({})); setSpError(d.error || 'Failed to change password.'); return }
                      } else {
                        const res = await authedFetch('/auth/set-password', { method: 'POST', body: JSON.stringify({ password: spPassword, confirmPassword: spConfirmPassword }) })
                        if (!res.ok) { const d = await res.json().catch(() => ({})); setSpError(d.error || 'Failed to set password.'); return }
                      }
                      setSpSuccess(true)
                    } catch { setSpError('Failed to update password. Please try again.') } finally { setSpLoading(false) }
                  }} disabled={spLoading || !spPassword || !spConfirmPassword || (userHasPassword && !spOldPassword)}>
                    {spLoading ? (userHasPassword ? 'Changing...' : 'Setting...') : (userHasPassword ? 'Change Password' : 'Set Password')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ── Change Email Modal ── */}
      {isChangeEmailModalOpen && createPortal(
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-gray-800">Change Email</h2>
            {ceSuccess ? (
              <div className="mt-4">
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm">
                  Email changed successfully! Please log out and log back in with your new email.
                </div>
                <div className="mt-4 flex justify-end"><Button onClick={() => setIsChangeEmailModalOpen(false)}>Close</Button></div>
              </div>
            ) : ceStep === 'email' ? (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-gray-600">Enter your new email address. We will send a verification code to confirm you own it.</p>
                <p className="text-xs text-amber-600">Note: Each account is limited to 3 email changes{ceChangesRemaining !== null ? ` (${ceChangesRemaining} remaining)` : ''}.</p>
                {ceError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{ceError}</div>}
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Email</label>
                  <input type="email" value={ceNewEmail} onChange={(e) => { setCeNewEmail(sanitizeInput(e.target.value, 254)); setCeError(null) }}
                    className="mt-1 text-gray-600 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter new email address" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm New Email</label>
                  <input type="email" value={ceConfirmEmail} onChange={(e) => { setCeConfirmEmail(sanitizeInput(e.target.value, 254)); setCeError(null) }}
                    className="mt-1 text-gray-600 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Confirm new email address" />
                </div>
                {ceConfirmEmail && ceNewEmail !== ceConfirmEmail && <p className="text-xs text-red-500">Emails do not match</p>}
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setIsChangeEmailModalOpen(false)} disabled={ceLoading}>Cancel</Button>
                  <Button onClick={async () => {
                    setCeError(null)
                    const n = ceNewEmail.trim().toLowerCase(), c = ceConfirmEmail.trim().toLowerCase()
                    const v = validateEmail(n)
                    if (!v.valid) { setCeError(v.error || 'Invalid email'); return }
                    if (n !== c) { setCeError('Emails do not match'); return }
                    if (n === user.email.trim().toLowerCase()) { setCeError('New email must be different from your current email'); return }
                    setCeLoading(true)
                    try {
                      const res = await authedFetch('/auth/change-email-start', { method: 'POST', body: JSON.stringify({ newEmail: n, confirmEmail: c }) })
                      const d = await res.json().catch(() => ({}))
                      if (typeof d.emailChangesRemaining === 'number') setCeChangesRemaining(d.emailChangesRemaining)
                      if (!res.ok) { setCeError(d.error || 'Failed to send verification code.'); return }
                      setCeStep('code'); setCeError(null)
                    } catch { setCeError('Failed to send verification code. Please try again.') } finally { setCeLoading(false) }
                  }} disabled={ceLoading || !ceNewEmail || !ceConfirmEmail}>{ceLoading ? 'Sending code...' : 'Send Verification Code'}</Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded text-sm">
                  We sent a 6-digit verification code to <strong>{ceNewEmail.trim().toLowerCase()}</strong>.
                </div>
                {ceError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{ceError}</div>}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Verification Code</label>
                  <input id="ce-code" type="text" inputMode="numeric" value={ceCode} onChange={(e) => { setCeCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setCeError(null) }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('ce-verify-btn')?.click() } }}
                    className="mt-1 text-gray-600 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest"
                    placeholder="000000" maxLength={6} />
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => { setCeStep('email'); setCeCode(''); setCeError(null) }} disabled={ceLoading}>Back</Button>
                  <Button id="ce-verify-btn" onClick={async () => {
                    setCeError(null)
                    if (!/^\d{6}$/.test(ceCode.trim())) { setCeError('Please enter a valid 6-digit code.'); return }
                    setCeLoading(true)
                    try {
                      const res = await authedFetch('/auth/change-email-confirm', { method: 'POST', body: JSON.stringify({ code: ceCode.trim() }) })
                      if (!res.ok) { const d = await res.json().catch(() => ({})); setCeError(d.error || 'Failed to verify code.'); return }
                      await refreshUser(); setCeSuccess(true)
                    } catch { setCeError('Failed to verify code. Please try again.') } finally { setCeLoading(false) }
                  }} disabled={ceLoading || ceCode.length !== 6}>{ceLoading ? 'Verifying...' : 'Verify & Change Email'}</Button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

function PasswordStrength({ password, confirmPassword }: { password: string; confirmPassword: string }) {
  return (
    <div className="text-xs text-gray-500 space-y-1">
      <p className={password.length >= 8 ? 'text-green-600' : ''}>{password.length >= 8 ? '✓' : '○'} At least 8 characters</p>
      <p className={/[a-z]/.test(password) ? 'text-green-600' : ''}>{/[a-z]/.test(password) ? '✓' : '○'} At least one lowercase letter</p>
      <p className={/[A-Z]/.test(password) ? 'text-green-600' : ''}>{/[A-Z]/.test(password) ? '✓' : '○'} At least one uppercase letter</p>
      <p className={/\d/.test(password) ? 'text-green-600' : ''}>{/\d/.test(password) ? '✓' : '○'} At least one number</p>
      <p className={/[^a-zA-Z0-9]/.test(password) ? 'text-green-600' : ''}>{/[^a-zA-Z0-9]/.test(password) ? '✓' : '○'} At least one special character</p>
      {confirmPassword && password !== confirmPassword && <p className="text-red-500">Passwords do not match</p>}
      {confirmPassword && password === confirmPassword && password.length > 0 && <p className="text-green-600">✓ Passwords match</p>}
    </div>
  )
}
